import { first, uniqBy } from 'lodash/array';
import { every, orderBy } from 'lodash/collection';
import { isEmpty, isEqual } from 'lodash/lang';
import { assignInWith, has } from 'lodash/object';
import { flow } from 'lodash/util';

import { FRS_LOADED, FRS_READY } from 'components/eui/EuiFiltersContext/reducer';

export const initializeFlow = flow([
    recalculateSelectedStudy,
    recalculateSites,
    recalculateSelectedSiteIds,
    recalculateSelectedSites,
    recalculateStudies
  ]),
  studiesUpdateFlow = flow([recalculateSelectedStudy, recalculateSites, recalculateSelectedSites]),
  sitesUpdateFlow = flow([recalculateSelectedSites, recalculateStudies, recalculateSelectedStudy]);

export function initializeProcessor(state, action) {
  const { payload } = action;
  const { key, studySites } = payload;
  const previousFilterState = state[key];
  const studies = getFilteredOrderedUniqueStudies(studySites);

  const selectedStudyId = resolveSelectedStudyId(payload, previousFilterState.selectedStudyId, studies);
  const sites = getFilteredOrderedUniqueSites(studySites, selectedStudyId);

  const newState = initializeFlow({
    ...previousFilterState,
    studySites,
    studies,
    selectedStudyId,
    sites,
    selectedSiteMap: {},
    selectedStudy: {},
    selectedSites: []
  });
  return {
    ...state,
    schema: {
      ...state.schema,
      [key]: {
        ...state.schema[key],
        status: FRS_LOADED
      }
    },
    [key]: newState
  };
}

function resolveSelectedStudyId(payload, previousSelectedStudyId, studies) {
  if (has(payload, 'selectedStudyId')) {
    return payload.selectedStudyId;
  }
  if (!previousSelectedStudyId) {
    return first(studies)?.id || null;
  }
  return previousSelectedStudyId;
}

export function syncCacheProcessor(state, action) {
  const { payload: cache } = action;

  const nextState = { ...state };

  if (!isEmpty(cache)) {
    nextState.ssu = initializeFlow({
      ...nextState.ssu,
      ...cache
    });
  }

  nextState.schema = {
    ...nextState.schema,
    ssu: {
      ...nextState.schema.ssu,
      status: FRS_READY
    }
  };

  return nextState;
}

export function setValueProcessor(state, action) {
  const { payload } = action;
  const { key, key2, value } = payload;
  const previousFilterState = state[key];
  const { selectedStudyId, selectedSiteMap } = previousFilterState;

  let newState;

  if (key2 === 'Study') {
    const studyId = value?.id || null;

    if (isEqual(studyId, selectedStudyId)) return state;

    newState = studiesUpdateFlow({ ...previousFilterState, selectedStudyId: studyId });
  }

  if (key2 === 'Site') {
    const siteIds = collectIdsMap(value);
    if (isEqual(siteIds, selectedSiteMap)) return state;
    newState = sitesUpdateFlow({ ...previousFilterState, selectedSiteMap: mergeMaps(siteIds, selectedSiteMap) });
  }

  return {
    ...state,
    [key]: newState
  };
}

function recalculateSelectedStudy(state) {
  const { studies, selectedStudyId } = state;
  const selectedStudy = studies.find(({ id }) => selectedStudyId === id) || null;
  return { ...state, selectedStudy };
}

function recalculateSelectedSites(state) {
  const { sites, selectedSiteMap } = state;
  const selectedSites = sites.filter(({ id }) => selectedSiteMap[id]);
  return { ...state, selectedSites };
}

function recalculateSites(state) {
  const { studySites, selectedStudyId } = state;
  return { ...state, sites: getFilteredOrderedUniqueSites(studySites, selectedStudyId) };
}

function recalculateStudies(state) {
  const { studySites, selectedSiteMap } = state;
  return { ...state, studies: getFilteredOrderedUniqueStudies(studySites, selectedSiteMap) };
}

function recalculateSelectedSiteIds(state) {
  const { sites, selectedSiteMap } = state;
  return {
    ...state,
    selectedSiteMap: sites.reduce(function(siteIdsMap, site) {
      siteIdsMap[site.id] = has(selectedSiteMap, site.id) ? selectedSiteMap[site.id] : false;
      return siteIdsMap;
    }, {})
  };
}

function getFilteredOrderedUniqueStudies(studySites, selectedSiteMap) {
  const studies = studySites.reduce(function(studies, { study, site }) {
    if (every(selectedSiteMap, val => !val) || selectedSiteMap[site.id]) {
      studies.push(study);
    }
    return studies;
  }, []);
  return orderBy(uniqBy(studies, 'id'), 'name', 'asc');
}

function getFilteredOrderedUniqueSites(studySites, selectedStudyId) {
  const sites = studySites.reduce(function(sites, { study, site }) {
    if (!selectedStudyId || selectedStudyId === study.id) {
      sites.push(site);
    }
    return sites;
  }, []);

  return orderBy(uniqBy(sites, 'id'), 'name', 'asc');
}

function collectIdsMap(item) {
  return item.reduce(function(accumulator, { id }) {
    accumulator[id] = true;
    return accumulator;
  }, {});
}

function mergeMaps(a, b) {
  return assignInWith(a, b, a => a || false);
}
